import CloseIcon from '@mui/icons-material/Close'
import RefreshIcon from '@mui/icons-material/Refresh'
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormHelperText,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
  styled,
} from '@mui/material'
import Button from '@mui/material/Button'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined'
import React from 'react'
import { useSelector } from 'react-redux'
import {
  API_PATH_ADMIN_RENTAL_NOTE_DELETE,
  API_PATH_ADMIN_RENTAL_NOTE_SAVE,
  API_PATH_ADMIN_RENTAL_VEHICLE_ATTACHMENT_DELETE,
  API_PATH_ADMIN_RENTAL_VEHICLE_ATTACHMENT_DOWNLOAD,
  API_PATH_ADMIN_RENTAL_VEHICLE_ATTACHMENT_UPLOAD,
  API_PATH_ADMIN_RENTAL_VEHICLE_DELETE,
  API_PATH_ADMIN_RENTAL_VEHICLE_SAVE,
  API_PATH_CREATE_PAYMENT,
  API_PATH_PROFILE_UPDATE,
  API_PATH_SHIPPING_UPDATE,
  API_PATH_SUBSCRIPTIONS_CANCEL,
  API_PATH_SUBSCRIPTIONS_CREATE,
  API_PATH_SUBSCRIPTIONS_UPDATE,
  API_PATH_TRANSACTIONS_CREATE,
  API_PATH_UPDATE_PAYMENT,
} from '../../../config/api'
import { STATES } from '../../../config/states'
import {
  getApiConfig,
  getAuthToken,
  getLocalServerURL,
} from '../../../utils/getApiConfig'
import {
  TextMaskCreditCard,
  TextMaskCreditCardDate,
  TextMaskPhoneNumber,
  TextMaskZipcode,
} from '../../../components/maskedInputs'
import { onError } from '../../../utils/onError'
import { onSuccess } from '../../../utils/onSuccess'
import { BankAccountTypeEnum } from '../../Profile/types'
import { getUnitsIndividual, getUnitsMaster } from '../../Units/selectors'
import { useAdminCustomerWorkbench } from './hooks/useCustomerWorkbench'
import {
  getRequiredCardNumberError,
  getRequiredEmailError,
  getRequiredExpirationDateError,
  getRequiredPhoneError,
  getRequiredTextError,
  getRequiredZipError,
} from '../validation'
import { dispatchAdminSubscriptionsOverviewRefresh } from '../events'
import type {
  AdminRentalVehicle,
} from './types'

const ContentStack = styled(Stack)(({ theme }) => ({
  gap: theme.spacing(2),
}))

const SectionCard = styled(Box)(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.spacing(1.5),
  padding: theme.spacing(2),
  backgroundColor:
    theme.palette.mode === 'dark'
      ? 'rgba(255, 255, 255, 0.02)'
      : theme.palette.background.paper,
}))

const SectionHeader = styled(Stack)(({ theme }) => ({
  gap: theme.spacing(0.5),
  marginBottom: theme.spacing(2),
}))

const ActionsRow = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: theme.spacing(1.5),
  marginTop: theme.spacing(2),
}))

const DetailList = styled(Stack)(({ theme }) => ({
  gap: theme.spacing(1.5),
  marginTop: theme.spacing(1.5),
}))

type PaymentMethod = 'creditCard' | 'bankAccount'
type IntervalUnit = 'months' | 'days'

type ProfileFormState = {
  email: string
  firstName: string
  lastName: string
  phoneNumber: string
  address: string
  city: string
  state: string
  zip: string
}

type PaymentFormState = {
  paymentMethod: PaymentMethod
  firstName: string
  lastName: string
  phoneNumber: string
  address: string
  city: string
  state: string
  zip: string
  email: string
  cardNumber: string
  expirationDate: string
  routingNumber: string
  accountNumber: string
  nameOnAccount: string
  accountType: BankAccountTypeEnum
}

type TransactionFormState = {
  customerPaymentProfileId: string
  amount: string
  description: string
  itemName: string
  itemDescription: string
  quantity: string
}

type SubscriptionFormState = {
  subscriptionId: string
  unitId: string
  customerPaymentProfileId: string
  customerAddressId: string
  name: string
  description: string
  amount: string
  startDate: string
  intervalLength: string
  intervalUnit: IntervalUnit
  totalOccurrences: string
  trialOccurrences: string
  invoiceNumber: string
}

type RentalNoteFormState = {
  noteId: string
  text: string
}

type RentalVehicleFormState = {
  vehicleId: string
  registrationNumber: string
}

interface Props {
  open: boolean
  onClose: () => void
  customerProfileId: number
  customerName: string
  initialTab?: CustomerWorkbenchTab
}

export type CustomerWorkbenchTab =
  | 'profile'
  | 'paymentProfiles'
  | 'transactions'
  | 'subscriptions'

const CUSTOMER_WORKBENCH_TAB_INDEX: Record<CustomerWorkbenchTab, number> = {
  profile: 0,
  paymentProfiles: 1,
  transactions: 2,
  subscriptions: 3,
}

const EMPTY_PROFILE_FORM: ProfileFormState = {
  email: '',
  firstName: '',
  lastName: '',
  phoneNumber: '',
  address: '',
  city: '',
  state: 'undefined',
  zip: '',
}

const EMPTY_PAYMENT_FORM: PaymentFormState = {
  paymentMethod: 'creditCard',
  firstName: '',
  lastName: '',
  phoneNumber: '',
  address: '',
  city: '',
  state: 'undefined',
  zip: '',
  email: '',
  cardNumber: '',
  expirationDate: '',
  routingNumber: '',
  accountNumber: '',
  nameOnAccount: '',
  accountType: BankAccountTypeEnum.CHECKING,
}

const EMPTY_TRANSACTION_FORM: TransactionFormState = {
  customerPaymentProfileId: '',
  amount: '',
  description: '',
  itemName: '',
  itemDescription: '',
  quantity: '1',
}

const EMPTY_SUBSCRIPTION_FORM: SubscriptionFormState = {
  subscriptionId: '',
  unitId: '',
  customerPaymentProfileId: '',
  customerAddressId: '',
  name: '',
  description: '',
  amount: '',
  startDate: '',
  intervalLength: '1',
  intervalUnit: 'months',
  totalOccurrences: '9999',
  trialOccurrences: '0',
  invoiceNumber: '',
}

const EMPTY_RENTAL_NOTE_FORM: RentalNoteFormState = {
  noteId: '',
  text: '',
}

const EMPTY_RENTAL_VEHICLE_FORM: RentalVehicleFormState = {
  vehicleId: '',
  registrationNumber: '',
}

const getMessageText = (data: any) =>
  data?.messages?.message?.[0]?.text ?? data?.messages?.message?.[0]?.code

const isOkResponse = (data: any) => data?.messages?.resultCode === 'Ok'

const trimNumberString = (value: string) => value.trim()

const toPositiveNumber = (value: string) => {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : 0
}

const hasText = (value: string) => value.trim().length > 0

const toDateInputValue = (value?: string | null) => {
  const normalized = String(value ?? '').trim()
  if (normalized === '') {
    return ''
  }

  const dateOnlyMatch = normalized.match(/^(\d{4}-\d{2}-\d{2})(?:[T\s].*)?$/)
  if (dateOnlyMatch != null) {
    return dateOnlyMatch[1]
  }

  const date = new Date(normalized)
  if (Number.isNaN(date.getTime())) {
    return normalized
  }

  return date.toISOString().slice(0, 10)
}

const formatDateTime = (value?: string | null) => {
  const normalized = String(value ?? '').trim()
  if (normalized === '') {
    return ''
  }

  const date = new Date(normalized)
  if (Number.isNaN(date.getTime())) {
    return normalized
  }

  return date.toLocaleString()
}

const toNormalizedUnitValue = (value?: string | null) =>
  String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '')

const buildAdminSubscriptionInvoiceNumber = ({
  customerProfileId,
  existingInvoiceNumber,
  unit,
}: {
  customerProfileId: number
  existingInvoiceNumber: string
  unit: SubscriptionUnitOption
}) => {
  if (existingInvoiceNumber.trim() !== '') {
    return existingInvoiceNumber.trim()
  }

  const normalizedSize = unit.size.replace(/[^a-zA-Z0-9]/g, '').toUpperCase()
  const normalizedUnitNumber = unit.unitNumber.replace(/[^a-zA-Z0-9]/g, '')

  return `ADM-${customerProfileId}-${normalizedSize}-${normalizedUnitNumber}`
}

const buildAdminTransactionInvoiceNumber = ({
  customerProfileId,
  customerPaymentProfileId,
}: {
  customerProfileId: number
  customerPaymentProfileId: string
}) => `ADMTXN-${customerProfileId}-${customerPaymentProfileId}-${Date.now()}`

const buildProfileForm = (
  profile: ProfileFormState,
  shippingAddress?: {
    firstName: string
    lastName: string
    phoneNumber: string
    address: string
    city: string
    state: string
    zip: string
  }
): ProfileFormState => ({
  email: profile.email,
  firstName: shippingAddress?.firstName || profile.firstName,
  lastName: shippingAddress?.lastName || profile.lastName,
  phoneNumber: shippingAddress?.phoneNumber || profile.phoneNumber,
  address: shippingAddress?.address || profile.address,
  city: shippingAddress?.city || profile.city,
  state: shippingAddress?.state || profile.state || 'undefined',
  zip: shippingAddress?.zip || profile.zip,
})

const buildPaymentForm = ({
  paymentMethod,
  billTo,
}: {
  paymentMethod: PaymentMethod
  billTo: {
    firstName: string
    lastName: string
    phoneNumber: string
    address: string
    city: string
    state: string
    zip: string
    email: string
  }
}): PaymentFormState => ({
  paymentMethod,
  firstName: billTo.firstName,
  lastName: billTo.lastName,
  phoneNumber: billTo.phoneNumber,
  address: billTo.address,
  city: billTo.city,
  state: billTo.state || 'undefined',
  zip: billTo.zip,
  email: billTo.email,
  cardNumber: '',
  expirationDate: '',
  routingNumber: '',
  accountNumber: '',
  nameOnAccount: '',
  accountType: BankAccountTypeEnum.CHECKING,
})

const buildSubscriptionForm = ({
  subscription,
  customerPaymentProfileId,
  customerAddressId,
  selectedUnitId,
}: {
  subscription?: {
    subscriptionId: string
    name: string
    amount: number
    profile?: {
      paymentProfile?: {
        customerPaymentProfileId: string
      }
      shippingProfile?: {
        customerAddressId: string
      }
    }
    startDate: string
    paymentSchedule: {
      interval: { length: number; unit: string }
      totalOccurrences: number
      trialOccurrences: number
      startDate: string
    }
    order: { description: string; invoiceNumber: string }
  }
  customerPaymentProfileId: string
  customerAddressId: string
  selectedUnitId: string
}): SubscriptionFormState => ({
  subscriptionId: subscription?.subscriptionId ?? '',
  unitId: selectedUnitId,
  customerPaymentProfileId:
    subscription?.profile?.paymentProfile?.customerPaymentProfileId ??
    customerPaymentProfileId,
  customerAddressId:
    subscription?.profile?.shippingProfile?.customerAddressId ??
    customerAddressId,
  name: subscription?.name ?? '',
  description: subscription?.order?.description ?? '',
  amount:
    subscription?.amount != null ? String(subscription.amount) : '',
  startDate: toDateInputValue(
    subscription?.paymentSchedule?.startDate ?? subscription?.startDate
  ),
  intervalLength:
    subscription?.paymentSchedule?.interval?.length != null
      ? String(subscription.paymentSchedule.interval.length)
      : '1',
  intervalUnit:
    subscription?.paymentSchedule?.interval?.unit === 'days' ? 'days' : 'months',
  totalOccurrences:
    subscription?.paymentSchedule?.totalOccurrences != null
      ? String(subscription.paymentSchedule.totalOccurrences)
      : '9999',
  trialOccurrences:
    subscription?.paymentSchedule?.trialOccurrences != null
      ? String(subscription.paymentSchedule.trialOccurrences)
      : '0',
  invoiceNumber: subscription?.order?.invoiceNumber ?? '',
})

type SubscriptionUnitOption = {
  id: string
  masterId: string
  size: string
  unitNumber: string
  label: string
  formattedValue: string
  price: number
  isClimate: boolean
  isCovered: boolean
  status: 'available' | 'assignedToCustomer'
  subscriptionId: string
}

export const CustomerWorkbenchModal: React.FC<Props> = ({
  open,
  onClose,
  customerProfileId,
  customerName,
  initialTab = 'profile',
}) => {
  const masterUnits = useSelector(getUnitsMaster)
  const individualUnits = useSelector(getUnitsIndividual)
  const {
    profile,
    paymentProfiles,
    shippingAddresses,
    transactions,
    subscriptions,
    rentalDetails,
    loading,
    error,
    refresh,
  } = useAdminCustomerWorkbench({
    customerProfileId,
    open,
  })
  const [tab, setTab] = React.useState(CUSTOMER_WORKBENCH_TAB_INDEX[initialTab])
  const [busyAction, setBusyAction] = React.useState<string | null>(null)
  const actionLockRef = React.useRef(false)
  const [selectedPaymentProfileId, setSelectedPaymentProfileId] =
    React.useState('')
  const [selectedShippingAddressId, setSelectedShippingAddressId] =
    React.useState('')
  const [selectedSubscriptionId, setSelectedSubscriptionId] =
    React.useState('')
  const [profileForm, setProfileForm] =
    React.useState<ProfileFormState>(EMPTY_PROFILE_FORM)
  const [paymentForm, setPaymentForm] =
    React.useState<PaymentFormState>(EMPTY_PAYMENT_FORM)
  const [transactionForm, setTransactionForm] =
    React.useState<TransactionFormState>(EMPTY_TRANSACTION_FORM)
  const [subscriptionForm, setSubscriptionForm] =
    React.useState<SubscriptionFormState>(EMPTY_SUBSCRIPTION_FORM)
  const [rentalNoteForm, setRentalNoteForm] =
    React.useState<RentalNoteFormState>(EMPTY_RENTAL_NOTE_FORM)
  const [rentalVehicleForm, setRentalVehicleForm] =
    React.useState<RentalVehicleFormState>(EMPTY_RENTAL_VEHICLE_FORM)
  const [attachmentAuthToken, setAttachmentAuthToken] = React.useState('')

  const masterPriceById = React.useMemo(
    () =>
      masterUnits.reduce<Record<string, number>>((accumulator, unit) => {
        if (unit.id != null) {
          accumulator[String(unit.id)] = Number(unit.price ?? 0)
        }
        return accumulator
      }, {}),
    [masterUnits]
  )

  const subscriptionUnitOptions = React.useMemo<SubscriptionUnitOption[]>(() => {
    return individualUnits
      .filter((unit) => {
        if (!unit.id || !unit.masterId || !unit.unitNumber) {
          return false
        }

        const unitCustomerProfileId =
          unit.customerProfileId == null ? '' : String(unit.customerProfileId)

        return (
          unitCustomerProfileId === '' ||
          unitCustomerProfileId === String(customerProfileId)
        )
      })
      .map((unit) => {
        const formattedValue = `${unit.size}/${unit.unitNumber}`
        const unitCustomerProfileId =
          unit.customerProfileId == null ? '' : String(unit.customerProfileId)

        return {
          id: String(unit.id),
          masterId: String(unit.masterId),
          size: unit.size,
          unitNumber: String(unit.unitNumber),
          label: formattedValue,
          formattedValue,
          price: Number(masterPriceById[String(unit.masterId)] ?? 0),
          isClimate: Boolean(unit.isClimate),
          isCovered: Boolean(unit.isCovered),
          status:
            unitCustomerProfileId === String(customerProfileId)
              ? ('assignedToCustomer' as const)
              : ('available' as const),
          subscriptionId: String(unit.subscriptionId ?? ''),
        }
      })
      .sort((left, right) => left.label.localeCompare(right.label))
  }, [customerProfileId, individualUnits, masterPriceById])

  const selectedPaymentProfile = React.useMemo(
    () =>
      paymentProfiles.find(
        (item) => item.customerPaymentProfileId === selectedPaymentProfileId
      ),
    [paymentProfiles, selectedPaymentProfileId]
  )

  const selectedShippingAddress = React.useMemo(
    () =>
      shippingAddresses.find(
        (item) => item.customerAddressId === selectedShippingAddressId
      ),
    [selectedShippingAddressId, shippingAddresses]
  )

  const selectedSubscription = React.useMemo(
    () =>
      subscriptions.find((item) => item.subscriptionId === selectedSubscriptionId),
    [selectedSubscriptionId, subscriptions]
  )

  const selectedSubscriptionUnit = React.useMemo(
    () =>
      subscriptionUnitOptions.find((item) => item.id === subscriptionForm.unitId) ??
      null,
    [subscriptionForm.unitId, subscriptionUnitOptions]
  )

  const selectedRentalDetail = React.useMemo(
    () =>
      rentalDetails.find(
        (detail) =>
          detail.subscriptionId ===
          String(selectedSubscription?.subscriptionId ?? '')
      ) ?? null,
    [rentalDetails, selectedSubscription]
  )

  const isCoveredParkingRental = Boolean(selectedSubscriptionUnit?.isCovered)

  React.useEffect(() => {
    if (!open) {
      setTab(CUSTOMER_WORKBENCH_TAB_INDEX[initialTab])
      setBusyAction(null)
      actionLockRef.current = false
      return
    }

    setTab(CUSTOMER_WORKBENCH_TAB_INDEX[initialTab])

    setSelectedPaymentProfileId((current) =>
      current && paymentProfiles.some((item) => item.customerPaymentProfileId === current)
        ? current
        : paymentProfiles[0]?.customerPaymentProfileId ?? ''
    )
    setSelectedShippingAddressId((current) =>
      current && shippingAddresses.some((item) => item.customerAddressId === current)
        ? current
        : shippingAddresses[0]?.customerAddressId ?? ''
    )
    setSelectedSubscriptionId((current) =>
      current && subscriptions.some((item) => item.subscriptionId === current)
        ? current
        : subscriptions[0]?.subscriptionId ?? ''
    )
  }, [initialTab, open, paymentProfiles, shippingAddresses, subscriptions])

  React.useEffect(() => {
    setProfileForm(
      buildProfileForm(
        {
          email: profile.email,
          firstName: profile.firstName,
          lastName: profile.lastName,
          phoneNumber: profile.phoneNumber,
          address: profile.address,
          city: profile.city,
          state: profile.state,
          zip: profile.zip,
        },
        selectedShippingAddress
      )
    )
  }, [profile, selectedShippingAddress])

  React.useEffect(() => {
    if (selectedPaymentProfile != null) {
      setPaymentForm(
        buildPaymentForm({
          paymentMethod:
            selectedPaymentProfile.paymentMethod === 'bankAccount'
              ? 'bankAccount'
              : 'creditCard',
          billTo: {
            firstName: selectedPaymentProfile.billTo.firstName,
            lastName: selectedPaymentProfile.billTo.lastName,
            phoneNumber: selectedPaymentProfile.billTo.phoneNumber,
            address: selectedPaymentProfile.billTo.address,
            city: selectedPaymentProfile.billTo.city,
            state: selectedPaymentProfile.billTo.state,
            zip: selectedPaymentProfile.billTo.zip,
            email: selectedPaymentProfile.billTo.email,
          },
        })
      )
      setTransactionForm((current) => ({
        ...current,
        customerPaymentProfileId: selectedPaymentProfile.customerPaymentProfileId,
      }))
      return
    }

    const defaultForm = buildPaymentForm({
      paymentMethod: 'creditCard',
      billTo: {
        firstName: profile.firstName,
        lastName: profile.lastName,
        phoneNumber: profile.phoneNumber,
        address: profile.address,
        city: profile.city,
        state: profile.state,
        zip: profile.zip,
        email: profile.email,
      },
    })
    setPaymentForm(defaultForm)
    setTransactionForm((current) => ({
      ...current,
      customerPaymentProfileId: '',
    }))
  }, [profile, selectedPaymentProfile])

  React.useEffect(() => {
    const matchedUnitOption =
      subscriptionUnitOptions.find(
        (item) =>
          selectedSubscription != null &&
          item.subscriptionId === selectedSubscription.subscriptionId
      ) ??
      subscriptionUnitOptions.find((item) => {
        if (selectedSubscription == null) {
          return false
        }

        const formattedSubscriptionValue = toNormalizedUnitValue(
          selectedSubscription.order?.description || selectedSubscription.name
        )

        return (
          formattedSubscriptionValue !== '' &&
          toNormalizedUnitValue(item.formattedValue) === formattedSubscriptionValue
        )
      }) ??
      null

    setSubscriptionForm(
      buildSubscriptionForm({
        subscription: selectedSubscription as any,
        customerPaymentProfileId:
          selectedPaymentProfileId || paymentProfiles[0]?.customerPaymentProfileId || '',
        customerAddressId:
          selectedShippingAddressId || shippingAddresses[0]?.customerAddressId || '',
        selectedUnitId: matchedUnitOption?.id ?? '',
      })
    )
  }, [
    paymentProfiles,
    selectedPaymentProfileId,
    selectedSubscription,
    selectedShippingAddressId,
    subscriptionUnitOptions,
    shippingAddresses,
  ])

  React.useEffect(() => {
    setRentalNoteForm(EMPTY_RENTAL_NOTE_FORM)
    setRentalVehicleForm(EMPTY_RENTAL_VEHICLE_FORM)
  }, [selectedSubscriptionId, selectedRentalDetail?.updatedAt])

  React.useEffect(() => {
    let active = true

    if (!open) {
      setAttachmentAuthToken('')
      return
    }

    void getAuthToken().then((token) => {
      if (active) {
        setAttachmentAuthToken(token ?? '')
      }
    })

    return () => {
      active = false
    }
  }, [open])

  const submitRequest = React.useCallback(
    async (path: string, body: Record<string, unknown>) => {
      const config = await getApiConfig({ body })
      if (config == null) {
        throw new Error('Could not authorize admin request')
      }
      const response = await fetch(getLocalServerURL(path), config)
      if (!response.ok) {
        throw new Error('Request failed')
      }
      const data = await response.json()
      if (!isOkResponse(data)) {
        throw new Error(getMessageText(data) || 'Unknown error')
      }
      return data
    },
    []
  )

  const runAction = React.useCallback(
    async (action: string, work: () => Promise<void>) => {
      if (actionLockRef.current) {
        return
      }

      actionLockRef.current = true
      setBusyAction(action)
      try {
        await work()
      } catch (submitError) {
        onError(
          submitError instanceof Error
            ? submitError.message
            : 'Admin workbench request failed'
        )
      } finally {
        actionLockRef.current = false
        setBusyAction(null)
      }
    },
    []
  )

  const onProfileFieldChange = React.useCallback(
    (key: keyof ProfileFormState, value: string) =>
      setProfileForm((current) => ({ ...current, [key]: value })),
    []
  )

  const onPaymentFieldChange = React.useCallback(
    (key: keyof PaymentFormState, value: string) =>
      setPaymentForm((current) => ({ ...current, [key]: value })),
    []
  )

  const onTransactionFieldChange = React.useCallback(
    (key: keyof TransactionFormState, value: string) =>
      setTransactionForm((current) => ({ ...current, [key]: value })),
    []
  )

  const onSubscriptionFieldChange = React.useCallback(
    (key: keyof SubscriptionFormState, value: string) =>
      setSubscriptionForm((current) => ({ ...current, [key]: value })),
    []
  )

  const onRentalNoteFieldChange = React.useCallback(
    (key: keyof RentalNoteFormState, value: string) =>
      setRentalNoteForm((current) => ({ ...current, [key]: value })),
    []
  )

  const onRentalVehicleFieldChange = React.useCallback(
    (key: keyof RentalVehicleFormState, value: string) =>
      setRentalVehicleForm((current) => ({ ...current, [key]: value })),
    []
  )

  const getAttachmentDownloadUrl = React.useCallback(
    (vehicleId: string, attachmentId: string) => {
      const params = new URLSearchParams({
        subscriptionId: String(selectedSubscription?.subscriptionId ?? ''),
        vehicleId,
        attachmentId,
      })

      if (attachmentAuthToken !== '') {
        params.set('authToken', attachmentAuthToken)
      }

      return `${getLocalServerURL(
        API_PATH_ADMIN_RENTAL_VEHICLE_ATTACHMENT_DOWNLOAD
      )}?${params.toString()}`
    },
    [attachmentAuthToken, selectedSubscription]
  )

  React.useEffect(() => {
    if (selectedSubscriptionUnit == null) {
      return
    }

    setSubscriptionForm((current) => ({
      ...current,
      unitId: selectedSubscriptionUnit.id,
      name: selectedSubscriptionUnit.formattedValue,
      description: selectedSubscriptionUnit.formattedValue,
      amount: String(selectedSubscriptionUnit.price),
    }))
  }, [selectedSubscriptionUnit])

  const subscriptionExpectedValue = selectedSubscriptionUnit?.formattedValue ?? ''
  const intervalLengthValue = Number(subscriptionForm.intervalLength)
  const profileEmailError = getRequiredEmailError(profileForm.email)
  const profileFirstNameError = selectedShippingAddressId
    ? getRequiredTextError(profileForm.firstName, 'First name')
    : ''
  const profileLastNameError = selectedShippingAddressId
    ? getRequiredTextError(profileForm.lastName, 'Last name')
    : ''
  const profilePhoneError = selectedShippingAddressId
    ? getRequiredPhoneError(profileForm.phoneNumber, 'Phone number')
    : ''
  const profileAddressError = selectedShippingAddressId
    ? getRequiredTextError(profileForm.address, 'Address')
    : ''
  const profileCityError = selectedShippingAddressId
    ? getRequiredTextError(profileForm.city, 'City')
    : ''
  const profileStateError =
    selectedShippingAddressId && profileForm.state === 'undefined'
      ? 'State is required.'
      : ''
  const profileZipError = selectedShippingAddressId
    ? getRequiredZipError(profileForm.zip, 'ZIP')
    : ''
  const profileValidationMessage = React.useMemo(() => {
    if (profileEmailError !== '') {
      return profileEmailError
    }

    if (!selectedShippingAddressId) {
      return ''
    }

    return (
      profileFirstNameError ||
      profileLastNameError ||
      profilePhoneError ||
      profileAddressError ||
      profileCityError ||
      profileStateError ||
      profileZipError ||
      ''
    )
  }, [
    profileAddressError,
    profileCityError,
    profileEmailError,
    profileFirstNameError,
    profileLastNameError,
    profilePhoneError,
    profileStateError,
    profileZipError,
    selectedShippingAddressId,
  ])
  const isProfileFormValid = profileValidationMessage === ''
  const paymentEmailError = getRequiredEmailError(paymentForm.email)
  const paymentPhoneError = getRequiredPhoneError(
    paymentForm.phoneNumber,
    'Phone number'
  )
  const paymentFirstNameError = getRequiredTextError(
    paymentForm.firstName,
    'First name'
  )
  const paymentLastNameError = getRequiredTextError(
    paymentForm.lastName,
    'Last name'
  )
  const paymentAddressError = getRequiredTextError(
    paymentForm.address,
    'Billing address'
  )
  const paymentCityError = getRequiredTextError(paymentForm.city, 'City')
  const paymentStateError =
    paymentForm.state === 'undefined' ? 'State is required.' : ''
  const paymentZipError = getRequiredZipError(paymentForm.zip, 'ZIP')
  const paymentCardNumberError =
    paymentForm.paymentMethod === 'creditCard'
      ? getRequiredCardNumberError(paymentForm.cardNumber)
      : ''
  const paymentExpirationError =
    paymentForm.paymentMethod === 'creditCard'
      ? getRequiredExpirationDateError(paymentForm.expirationDate)
      : ''
  const paymentRoutingNumberError =
    paymentForm.paymentMethod === 'bankAccount' &&
    !hasText(paymentForm.routingNumber)
      ? 'Routing number is required.'
      : ''
  const paymentAccountNumberError =
    paymentForm.paymentMethod === 'bankAccount' &&
    !hasText(paymentForm.accountNumber)
      ? 'Account number is required.'
      : ''
  const paymentNameOnAccountError =
    paymentForm.paymentMethod === 'bankAccount' &&
    !hasText(paymentForm.nameOnAccount)
      ? 'Name on account is required.'
      : ''
  const paymentValidationMessage = React.useMemo(() => {
    return (
      paymentFirstNameError ||
      paymentLastNameError ||
      paymentPhoneError ||
      paymentEmailError ||
      paymentAddressError ||
      paymentCityError ||
      paymentStateError ||
      paymentZipError ||
      paymentRoutingNumberError ||
      paymentAccountNumberError ||
      paymentNameOnAccountError ||
      paymentCardNumberError ||
      paymentExpirationError ||
      ''
    )
  }, [
    paymentAccountNumberError,
    paymentAddressError,
    paymentCardNumberError,
    paymentCityError,
    paymentEmailError,
    paymentExpirationError,
    paymentFirstNameError,
    paymentLastNameError,
    paymentNameOnAccountError,
    paymentPhoneError,
    paymentRoutingNumberError,
    paymentStateError,
    paymentZipError,
  ])
  const isPaymentFormValid = paymentValidationMessage === ''
  const transactionPaymentProfileId =
    transactionForm.customerPaymentProfileId || selectedPaymentProfileId
  const transactionAmountValue = Number(transactionForm.amount)
  const transactionQuantityValue = Number(transactionForm.quantity)
  const transactionValidationMessage = React.useMemo(() => {
    if (!transactionPaymentProfileId) {
      return 'Select a payment profile before creating a transaction.'
    }

    if (!hasText(transactionForm.amount) || !Number.isFinite(transactionAmountValue)) {
      return 'Enter a valid amount.'
    }

    if (transactionAmountValue <= 0) {
      return 'Amount must be greater than zero.'
    }

    if (!hasText(transactionForm.itemName)) {
      return 'Item name is required.'
    }

    if (!hasText(transactionForm.quantity) || !Number.isFinite(transactionQuantityValue)) {
      return 'Enter a valid quantity.'
    }

    if (!Number.isInteger(transactionQuantityValue) || transactionQuantityValue <= 0) {
      return 'Quantity must be a whole number greater than zero.'
    }

    if (!hasText(transactionForm.description)) {
      return 'Description is required.'
    }

    return ''
  }, [
    transactionAmountValue,
    transactionForm.amount,
    transactionForm.description,
    transactionForm.itemName,
    transactionForm.quantity,
    transactionPaymentProfileId,
    transactionQuantityValue,
  ])
  const isTransactionFormValid = transactionValidationMessage === ''

  const subscriptionValidationMessage = React.useMemo(() => {
    if (!subscriptionForm.customerPaymentProfileId || !subscriptionForm.customerAddressId) {
      return 'Select payment and shipping profiles before managing a subscription.'
    }

    if (!subscriptionForm.unitId || selectedSubscriptionUnit == null) {
      return 'Select a unit first. Subscription name, description, and price come from that unit.'
    }

    if (!hasText(subscriptionForm.startDate)) {
      return 'Start date is required.'
    }

    if (!Number.isInteger(intervalLengthValue) || intervalLengthValue <= 0) {
      return 'Interval length must be a whole number greater than zero.'
    }

    if (
      subscriptionForm.name !== subscriptionExpectedValue ||
      subscriptionForm.description !== subscriptionExpectedValue
    ) {
      return 'Subscription name and description must match the selected unit.'
    }

    return ''
  }, [
    intervalLengthValue,
    selectedSubscriptionUnit,
    subscriptionExpectedValue,
    subscriptionForm.customerAddressId,
    subscriptionForm.customerPaymentProfileId,
    subscriptionForm.description,
    subscriptionForm.name,
    subscriptionForm.startDate,
    subscriptionForm.unitId,
  ])

  const isSubscriptionFormValid = subscriptionValidationMessage === ''

  const handleProfileSave = React.useCallback(() => {
    if (!isProfileFormValid) {
      onError(profileValidationMessage || 'Complete the profile form before saving.')
      return
    }

    void runAction('profile', async () => {
      await submitRequest(API_PATH_PROFILE_UPDATE, {
        customerProfileId: String(customerProfileId),
        email: profileForm.email.trim(),
      })

      if (selectedShippingAddressId) {
        await submitRequest(API_PATH_SHIPPING_UPDATE, {
          customerProfileId: String(customerProfileId),
          customerAddressId: selectedShippingAddressId,
          email: profileForm.email.trim(),
          firstName: profileForm.firstName.trim(),
          lastName: profileForm.lastName.trim(),
          phoneNumber: profileForm.phoneNumber.trim(),
          address: profileForm.address.trim(),
          city: profileForm.city.trim(),
          state: profileForm.state,
          zip: profileForm.zip.trim(),
          country: 'USA',
        })
      }

      onSuccess('Customer profile updated')
      refresh()
    })
  }, [
    customerProfileId,
    isProfileFormValid,
    profileForm,
    profileValidationMessage,
    refresh,
    runAction,
    selectedShippingAddressId,
    submitRequest,
  ])

  const handlePaymentCreate = React.useCallback(() => {
    if (!isPaymentFormValid) {
      onError(
        paymentValidationMessage || 'Complete the billing profile fields before saving'
      )
      return
    }

    void runAction('payment-create', async () => {
      const body: Record<string, unknown> = {
        customerProfileId: String(customerProfileId),
        firstName: paymentForm.firstName.trim(),
        lastName: paymentForm.lastName.trim(),
        phoneNumber: paymentForm.phoneNumber.trim(),
        address: paymentForm.address.trim(),
        city: paymentForm.city.trim(),
        state: paymentForm.state,
        zip: paymentForm.zip.trim(),
        email: paymentForm.email.trim(),
        country: 'USA',
      }

      if (paymentForm.paymentMethod === 'bankAccount') {
        body.routingNumber = trimNumberString(paymentForm.routingNumber)
        body.accountNumber = trimNumberString(paymentForm.accountNumber)
        body.nameOnAccount = paymentForm.nameOnAccount.trim()
        body.accountType = paymentForm.accountType
      } else {
        body.cardNumber = trimNumberString(paymentForm.cardNumber)
        body.expirationDate = trimNumberString(paymentForm.expirationDate)
      }

      await submitRequest(API_PATH_CREATE_PAYMENT, body)
      onSuccess('Payment profile created')
      refresh()
    })
  }, [
    customerProfileId,
    paymentForm,
    isPaymentFormValid,
    paymentValidationMessage,
    refresh,
    runAction,
    submitRequest,
  ])

  const handlePaymentUpdate = React.useCallback(() => {
    if (!selectedPaymentProfileId) {
      onError('Select a payment profile to update')
      return
    }

    if (!isPaymentFormValid) {
      onError(
        paymentValidationMessage || 'Complete the billing profile fields before saving'
      )
      return
    }

    void runAction('payment-update', async () => {
      const body: Record<string, unknown> = {
        customerProfileId: String(customerProfileId),
        customerPaymentProfileId: selectedPaymentProfileId,
        firstName: paymentForm.firstName.trim(),
        lastName: paymentForm.lastName.trim(),
        phoneNumber: paymentForm.phoneNumber.trim(),
        address: paymentForm.address.trim(),
        city: paymentForm.city.trim(),
        state: paymentForm.state,
        zip: paymentForm.zip.trim(),
        email: paymentForm.email.trim(),
        country: 'USA',
      }

      if (paymentForm.paymentMethod === 'bankAccount') {
        body.routingNumber = trimNumberString(paymentForm.routingNumber)
        body.accountNumber = trimNumberString(paymentForm.accountNumber)
        body.nameOnAccount = paymentForm.nameOnAccount.trim()
        body.accountType = paymentForm.accountType
      } else {
        body.cardNumber = trimNumberString(paymentForm.cardNumber)
        body.expirationDate = trimNumberString(paymentForm.expirationDate)
      }

      await submitRequest(API_PATH_UPDATE_PAYMENT, body)
      onSuccess('Payment profile updated')
      refresh()
    })
  }, [
    customerProfileId,
    paymentForm,
    isPaymentFormValid,
    paymentValidationMessage,
    refresh,
    runAction,
    selectedPaymentProfileId,
    submitRequest,
  ])

  const handleTransactionCreate = React.useCallback(() => {
    if (!transactionPaymentProfileId || !isTransactionFormValid) {
      onError(
        transactionValidationMessage ||
          'Complete the transaction form before creating it.'
      )
      return
    }

    void runAction('transaction-create', async () => {
      await submitRequest(API_PATH_TRANSACTIONS_CREATE, {
        customerProfileId,
        customerPaymentProfileId: Number(transactionPaymentProfileId),
        amount: transactionForm.amount.trim(),
        description: transactionForm.description.trim(),
        itemName: transactionForm.itemName.trim(),
        itemDescription: transactionForm.itemDescription.trim() || undefined,
        invoiceNumber: buildAdminTransactionInvoiceNumber({
          customerProfileId,
          customerPaymentProfileId: transactionPaymentProfileId,
        }),
        quantity: transactionQuantityValue,
      })

      onSuccess('Transaction created')
      dispatchAdminSubscriptionsOverviewRefresh()
      refresh()
    })
  }, [
    customerProfileId,
    refresh,
    runAction,
    submitRequest,
    isTransactionFormValid,
    transactionForm,
    transactionPaymentProfileId,
    transactionQuantityValue,
    transactionValidationMessage,
  ])

  const handleSubscriptionCreate = React.useCallback(() => {
    const paymentProfileId =
      subscriptionForm.customerPaymentProfileId || selectedPaymentProfileId
    const shippingAddressId =
      subscriptionForm.customerAddressId || selectedShippingAddressId

    if (!isSubscriptionFormValid || !selectedSubscriptionUnit) {
      onError(subscriptionValidationMessage || 'Complete the subscription form.')
      return
    }

    void runAction('subscription-create', async () => {
      const invoiceNumber = buildAdminSubscriptionInvoiceNumber({
        customerProfileId,
        existingInvoiceNumber: subscriptionForm.invoiceNumber,
        unit: selectedSubscriptionUnit,
      })

      await submitRequest(API_PATH_SUBSCRIPTIONS_CREATE, {
        customerProfileId,
        customerPaymentProfileId: Number(paymentProfileId),
        customerAddressId: Number(shippingAddressId),
        lastName: profile.lastName,
        email: profile.email,
        unitNumber: selectedSubscriptionUnit.unitNumber,
        size: selectedSubscriptionUnit.size,
        description: selectedSubscriptionUnit.formattedValue,
        total: selectedSubscriptionUnit.price,
        amount: selectedSubscriptionUnit.price,
        name: selectedSubscriptionUnit.formattedValue,
        startDate: toDateInputValue(subscriptionForm.startDate),
        intervalLength: toPositiveNumber(subscriptionForm.intervalLength),
        intervalUnit: 'months',
        totalOccurrences: 9999,
        trialOccurrences: 0,
        invoiceNumber,
        suppressEmail: true,
      })

      onSuccess('Subscription created')
      refresh()
    })
  }, [
    customerProfileId,
    isSubscriptionFormValid,
    profile.email,
    profile.email,
    profile.lastName,
    refresh,
    runAction,
    selectedSubscriptionUnit,
    selectedPaymentProfileId,
    selectedShippingAddressId,
    subscriptionValidationMessage,
    submitRequest,
    subscriptionForm,
  ])

  const handleSubscriptionUpdate = React.useCallback(() => {
    if (!subscriptionForm.subscriptionId) {
      onError('Select a subscription to update')
      return
    }

    if (!isSubscriptionFormValid || !selectedSubscriptionUnit) {
      onError(subscriptionValidationMessage || 'Complete the subscription form.')
      return
    }

    void runAction('subscription-update', async () => {
      await submitRequest(API_PATH_SUBSCRIPTIONS_UPDATE, {
        subscriptionId: subscriptionForm.subscriptionId,
        description: selectedSubscriptionUnit.formattedValue,
        name: selectedSubscriptionUnit.formattedValue,
        amount: selectedSubscriptionUnit.price,
        startDate: toDateInputValue(subscriptionForm.startDate),
        intervalLength: toPositiveNumber(subscriptionForm.intervalLength),
        intervalUnit: 'months',
        totalOccurrences: 9999,
        trialOccurrences: 0,
        customerProfileId,
        customerPaymentProfileId: Number(
          subscriptionForm.customerPaymentProfileId || selectedPaymentProfileId
        ),
        customerAddressId: Number(
          subscriptionForm.customerAddressId || selectedShippingAddressId
        ),
      })

      onSuccess('Subscription updated')
      refresh()
    })
  }, [
    customerProfileId,
    isSubscriptionFormValid,
    refresh,
    runAction,
    selectedSubscriptionUnit,
    selectedPaymentProfileId,
    selectedShippingAddressId,
    subscriptionValidationMessage,
    submitRequest,
    subscriptionForm,
  ])

  const handleSubscriptionCancel = React.useCallback(() => {
    if (!subscriptionForm.subscriptionId) {
      onError('Select a subscription to cancel')
      return
    }
    void runAction('subscription-cancel', async () => {
      await submitRequest(API_PATH_SUBSCRIPTIONS_CANCEL, {
        subscriptionId: subscriptionForm.subscriptionId,
      })

      onSuccess('Subscription cancelled')
      refresh()
    })
  }, [refresh, runAction, submitRequest, subscriptionForm.subscriptionId])

  const handleRentalNoteSave = React.useCallback(() => {
    if (!selectedSubscription?.subscriptionId || !selectedSubscriptionUnit) {
      onError('Select an existing subscription before saving notes')
      return
    }

    if (!hasText(rentalNoteForm.text)) {
      onError('Note text is required')
      return
    }

    void runAction('rental-note-save', async () => {
      await submitRequest(API_PATH_ADMIN_RENTAL_NOTE_SAVE, {
        subscriptionId: String(selectedSubscription.subscriptionId),
        customerProfileId,
        unitId: selectedSubscriptionUnit.id,
        unitLabel: selectedSubscriptionUnit.formattedValue,
        noteId: rentalNoteForm.noteId || undefined,
        text: rentalNoteForm.text.trim(),
      })

      setRentalNoteForm(EMPTY_RENTAL_NOTE_FORM)
      onSuccess(
        rentalNoteForm.noteId ? 'Rental note updated' : 'Rental note added'
      )
      refresh()
    })
  }, [
    customerProfileId,
    refresh,
    rentalNoteForm.noteId,
    rentalNoteForm.text,
    runAction,
    selectedSubscription,
    selectedSubscriptionUnit,
    submitRequest,
  ])

  const handleRentalNoteDelete = React.useCallback(
    (noteId: string) => {
      if (!selectedSubscription?.subscriptionId) {
        onError('Select an existing subscription before deleting notes')
        return
      }

      void runAction('rental-note-delete', async () => {
        await submitRequest(API_PATH_ADMIN_RENTAL_NOTE_DELETE, {
          subscriptionId: String(selectedSubscription.subscriptionId),
          noteId,
        })

        if (rentalNoteForm.noteId === noteId) {
          setRentalNoteForm(EMPTY_RENTAL_NOTE_FORM)
        }
        onSuccess('Rental note deleted')
        refresh()
      })
    },
    [refresh, rentalNoteForm.noteId, runAction, selectedSubscription, submitRequest]
  )

  const handleRentalVehicleSave = React.useCallback(() => {
    if (!selectedSubscription?.subscriptionId || !selectedSubscriptionUnit) {
      onError('Select an existing subscription before saving vehicle details')
      return
    }

    if (!isCoveredParkingRental) {
      onError('Vehicle registrations are only available for covered parking rentals')
      return
    }

    if (!hasText(rentalVehicleForm.registrationNumber)) {
      onError('Vehicle registration number is required')
      return
    }

    void runAction('rental-vehicle-save', async () => {
      await submitRequest(API_PATH_ADMIN_RENTAL_VEHICLE_SAVE, {
        subscriptionId: String(selectedSubscription.subscriptionId),
        customerProfileId,
        unitId: selectedSubscriptionUnit.id,
        unitLabel: selectedSubscriptionUnit.formattedValue,
        vehicleId: rentalVehicleForm.vehicleId || undefined,
        registrationNumber: rentalVehicleForm.registrationNumber.trim(),
      })

      setRentalVehicleForm(EMPTY_RENTAL_VEHICLE_FORM)
      onSuccess(
        rentalVehicleForm.vehicleId
          ? 'Vehicle registration updated'
          : 'Vehicle registration added'
      )
      refresh()
    })
  }, [
    customerProfileId,
    isCoveredParkingRental,
    refresh,
    rentalVehicleForm.registrationNumber,
    rentalVehicleForm.vehicleId,
    runAction,
    selectedSubscription,
    selectedSubscriptionUnit,
    submitRequest,
  ])

  const handleRentalVehicleDelete = React.useCallback(
    (vehicleId: string) => {
      if (!selectedSubscription?.subscriptionId) {
        onError('Select an existing subscription before deleting vehicle details')
        return
      }

      void runAction('rental-vehicle-delete', async () => {
        await submitRequest(API_PATH_ADMIN_RENTAL_VEHICLE_DELETE, {
          subscriptionId: String(selectedSubscription.subscriptionId),
          vehicleId,
        })

        if (rentalVehicleForm.vehicleId === vehicleId) {
          setRentalVehicleForm(EMPTY_RENTAL_VEHICLE_FORM)
        }
        onSuccess('Vehicle registration deleted')
        refresh()
      })
    },
    [
      refresh,
      rentalVehicleForm.vehicleId,
      runAction,
      selectedSubscription,
      submitRequest,
    ]
  )

  const handleVehicleAttachmentUpload = React.useCallback(
    async (vehicle: AdminRentalVehicle, file: File) => {
      if (!selectedSubscription?.subscriptionId) {
        onError('Select an existing subscription before uploading an attachment')
        return
      }

      if (!file.type.startsWith('image/')) {
        onError('Only image files are supported')
        return
      }

      if (file.size > 5 * 1024 * 1024) {
        onError('Attachment exceeds the 5MB size limit')
        return
      }

      const dataBase64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader()
        reader.onerror = () => reject(new Error('Could not read the attachment'))
        reader.onload = () => {
          const result = typeof reader.result === 'string' ? reader.result : ''
          resolve(result.split(',')[1] ?? '')
        }
        reader.readAsDataURL(file)
      })

      void runAction('rental-attachment-upload', async () => {
        await submitRequest(API_PATH_ADMIN_RENTAL_VEHICLE_ATTACHMENT_UPLOAD, {
          subscriptionId: String(selectedSubscription.subscriptionId),
          vehicleId: vehicle.id,
          fileName: file.name,
          contentType: file.type,
          dataBase64,
        })

        onSuccess('Vehicle photo uploaded')
        refresh()
      })
    },
    [refresh, runAction, selectedSubscription, submitRequest]
  )

  const handleVehicleAttachmentDelete = React.useCallback(
    (vehicleId: string, attachmentId: string) => {
      if (!selectedSubscription?.subscriptionId) {
        onError('Select an existing subscription before deleting an attachment')
        return
      }

      void runAction('rental-attachment-delete', async () => {
        await submitRequest(API_PATH_ADMIN_RENTAL_VEHICLE_ATTACHMENT_DELETE, {
          subscriptionId: String(selectedSubscription.subscriptionId),
          vehicleId,
          attachmentId,
        })

        onSuccess('Vehicle photo deleted')
        refresh()
      })
    },
    [refresh, runAction, selectedSubscription, submitRequest]
  )

  const currentPaymentSummary =
    selectedPaymentProfile?.paymentMethod === 'bankAccount'
      ? selectedPaymentProfile.bankAccount.accountNumber
      : selectedPaymentProfile?.creditCard.cardNumber

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
      <DialogTitle sx={{ pr: 6 }}>
        {`${customerName} customer workbench`}
      </DialogTitle>
      <IconButton
        aria-label="close customer workbench"
        onClick={onClose}
        sx={{ position: 'absolute', top: 8, right: 8 }}
      >
        <CloseIcon />
      </IconButton>
      <DialogContent dividers sx={{ p: 0 }}>
        <Box sx={{ px: 3, py: 2 }}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={1.5}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', md: 'center' }}
          >
            <div>
              <Typography variant="body1" fontWeight={700}>
                {profile.fullName || customerName}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {`Customer #${customerProfileId}`}
              </Typography>
            </div>
            <Button
              startIcon={
                busyAction === 'refresh' ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <RefreshIcon />
                )
              }
              onClick={() =>
                void runAction('refresh', async () => {
                  refresh()
                })
              }
              disabled={busyAction != null}
            >
              Refresh
            </Button>
          </Stack>
        </Box>
        <Divider />
        <Tabs
          value={tab}
          onChange={(_, value) => setTab(value)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ px: 2 }}
        >
          <Tab label="Profile" />
          <Tab label="Payment Profiles" />
          <Tab label="Transactions" />
          <Tab label="Subscriptions" />
        </Tabs>
        <Divider />
        <Box sx={{ p: 3 }}>
          {loading ? (
            <Stack spacing={2} alignItems="center" justifyContent="center" sx={{ minHeight: 240 }}>
              <CircularProgress size={28} />
              <Typography variant="body2" color="text.secondary">
                Loading customer workbench...
              </Typography>
            </Stack>
          ) : error ? (
            <Alert severity="error">{error}</Alert>
          ) : (
            <ContentStack>
              {tab === 0 ? (
                <SectionCard>
                  <SectionHeader>
                    <Typography variant="h6">Profile Information</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Update customer email and the selected shipping profile details.
                    </Typography>
                  </SectionHeader>
                  {shippingAddresses.length === 0 ? (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                      This customer has no shipping profiles on file. Email updates still work, but address updates require an existing shipping profile.
                    </Alert>
                  ) : (
                    <FormControl fullWidth sx={{ mb: 2 }}>
                      <InputLabel id="shipping-profile-label">Shipping profile</InputLabel>
                      <Select
                        labelId="shipping-profile-label"
                        label="Shipping profile"
                        value={selectedShippingAddressId}
                        onChange={(event) =>
                          setSelectedShippingAddressId(String(event.target.value))
                        }
                      >
                        {shippingAddresses.map((address) => (
                          <MenuItem
                            key={address.customerAddressId}
                            value={address.customerAddressId}
                          >
                            {`${address.firstName} ${address.lastName} • ${address.address}`}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="Email"
                        fullWidth
                        value={profileForm.email}
                        error={profileForm.email.trim() !== '' && profileEmailError !== ''}
                        helperText={
                          profileForm.email.trim() !== '' && profileEmailError !== ''
                            ? profileEmailError
                            : ' '
                        }
                        onChange={(event) =>
                          onProfileFieldChange('email', event.target.value)
                        }
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="Phone Number"
                        fullWidth
                        value={profileForm.phoneNumber}
                        error={
                          profileForm.phoneNumber.trim() !== '' &&
                          profilePhoneError !== ''
                        }
                        helperText={
                          profileForm.phoneNumber.trim() !== '' &&
                          profilePhoneError !== ''
                            ? profilePhoneError
                            : ' '
                        }
                        onChange={(event) =>
                          onProfileFieldChange('phoneNumber', event.target.value)
                        }
                        InputProps={{
                          inputComponent: TextMaskPhoneNumber as any,
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="First Name"
                        fullWidth
                        value={profileForm.firstName}
                        onChange={(event) =>
                          onProfileFieldChange('firstName', event.target.value)
                        }
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="Last Name"
                        fullWidth
                        value={profileForm.lastName}
                        onChange={(event) =>
                          onProfileFieldChange('lastName', event.target.value)
                        }
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        label="Address"
                        fullWidth
                        value={profileForm.address}
                        onChange={(event) =>
                          onProfileFieldChange('address', event.target.value)
                        }
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        label="City"
                        fullWidth
                        value={profileForm.city}
                        onChange={(event) =>
                          onProfileFieldChange('city', event.target.value)
                        }
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <FormControl fullWidth>
                        <InputLabel id="profile-state-label">State</InputLabel>
                        <Select
                          labelId="profile-state-label"
                          label="State"
                          value={profileForm.state}
                          onChange={(event) =>
                            onProfileFieldChange('state', String(event.target.value))
                          }
                        >
                          {STATES.map((state) => (
                            <MenuItem key={state.value} value={state.value}>
                              {state.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        label="Zip"
                        fullWidth
                        value={profileForm.zip}
                        error={profileForm.zip.trim() !== '' && profileZipError !== ''}
                        helperText={
                          profileForm.zip.trim() !== '' && profileZipError !== ''
                            ? profileZipError
                            : ' '
                        }
                        onChange={(event) =>
                          onProfileFieldChange('zip', event.target.value)
                        }
                        InputProps={{
                          inputComponent: TextMaskZipcode as any,
                        }}
                      />
                    </Grid>
                  </Grid>
                  <ActionsRow>
                    <Typography variant="caption" color="text.secondary">
                      Email updates Authorize.Net customer data. Address updates apply to the selected shipping profile.
                    </Typography>
                    <Button
                      variant="contained"
                      onClick={handleProfileSave}
                      disabled={busyAction != null || !isProfileFormValid}
                    >
                      {busyAction === 'profile' ? 'Saving...' : 'Save profile'}
                    </Button>
                  </ActionsRow>
                </SectionCard>
              ) : null}

              {tab === 1 ? (
                <SectionCard>
                  <SectionHeader>
                    <Typography variant="h6">Payment Profiles</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Choose an existing payment profile to update, or clear the selection and create a new one.
                    </Typography>
                  </SectionHeader>
                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 2 }}>
                    <FormControl fullWidth>
                      <InputLabel id="payment-profile-label">Existing payment profile</InputLabel>
                      <Select
                        labelId="payment-profile-label"
                        label="Existing payment profile"
                        value={selectedPaymentProfileId}
                        onChange={(event) =>
                          setSelectedPaymentProfileId(String(event.target.value))
                        }
                      >
                        <MenuItem value="">Create new payment profile</MenuItem>
                        {paymentProfiles.map((paymentProfile) => (
                          <MenuItem
                            key={paymentProfile.customerPaymentProfileId}
                            value={paymentProfile.customerPaymentProfileId}
                          >
                            {`#${paymentProfile.customerPaymentProfileId} • ${
                              paymentProfile.paymentMethod === 'bankAccount'
                                ? paymentProfile.bankAccount.accountNumber
                                : paymentProfile.creditCard.cardNumber
                            }`}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <FormControl fullWidth>
                      <InputLabel id="payment-method-label">Payment method</InputLabel>
                      <Select
                        labelId="payment-method-label"
                        label="Payment method"
                        value={paymentForm.paymentMethod}
                        onChange={(event) =>
                          onPaymentFieldChange(
                            'paymentMethod',
                            String(event.target.value)
                          )
                        }
                      >
                        <MenuItem value="creditCard">Credit card</MenuItem>
                        <MenuItem value="bankAccount">Bank account</MenuItem>
                      </Select>
                    </FormControl>
                  </Stack>
                  {selectedPaymentProfileId && currentPaymentSummary ? (
                    <Alert severity="info" sx={{ mb: 2 }}>
                      {`Current payment summary: ${currentPaymentSummary}. Enter full replacement details below before updating.`}
                    </Alert>
                  ) : null}
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="First Name"
                        fullWidth
                        value={paymentForm.firstName}
                        onChange={(event) =>
                          onPaymentFieldChange('firstName', event.target.value)
                        }
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="Last Name"
                        fullWidth
                        value={paymentForm.lastName}
                        onChange={(event) =>
                          onPaymentFieldChange('lastName', event.target.value)
                        }
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="Phone Number"
                        fullWidth
                        value={paymentForm.phoneNumber}
                        error={
                          paymentForm.phoneNumber.trim() !== '' &&
                          paymentPhoneError !== ''
                        }
                        helperText={
                          paymentForm.phoneNumber.trim() !== '' &&
                          paymentPhoneError !== ''
                            ? paymentPhoneError
                            : ' '
                        }
                        onChange={(event) =>
                          onPaymentFieldChange('phoneNumber', event.target.value)
                        }
                        InputProps={{
                          inputComponent: TextMaskPhoneNumber as any,
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="Email"
                        fullWidth
                        value={paymentForm.email}
                        error={paymentForm.email.trim() !== '' && paymentEmailError !== ''}
                        helperText={
                          paymentForm.email.trim() !== '' && paymentEmailError !== ''
                            ? paymentEmailError
                            : ' '
                        }
                        onChange={(event) =>
                          onPaymentFieldChange('email', event.target.value)
                        }
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        label="Billing Address"
                        fullWidth
                        value={paymentForm.address}
                        onChange={(event) =>
                          onPaymentFieldChange('address', event.target.value)
                        }
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        label="City"
                        fullWidth
                        value={paymentForm.city}
                        onChange={(event) =>
                          onPaymentFieldChange('city', event.target.value)
                        }
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <FormControl fullWidth>
                        <InputLabel id="payment-state-label">State</InputLabel>
                        <Select
                          labelId="payment-state-label"
                          label="State"
                          value={paymentForm.state}
                          onChange={(event) =>
                            onPaymentFieldChange('state', String(event.target.value))
                          }
                        >
                          {STATES.map((state) => (
                            <MenuItem key={state.value} value={state.value}>
                              {state.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        label="Zip"
                        fullWidth
                        value={paymentForm.zip}
                        error={paymentForm.zip.trim() !== '' && paymentZipError !== ''}
                        helperText={
                          paymentForm.zip.trim() !== '' && paymentZipError !== ''
                            ? paymentZipError
                            : ' '
                        }
                        onChange={(event) =>
                          onPaymentFieldChange('zip', event.target.value)
                        }
                        InputProps={{
                          inputComponent: TextMaskZipcode as any,
                        }}
                      />
                    </Grid>
                    {paymentForm.paymentMethod === 'bankAccount' ? (
                      <>
                        <Grid item xs={12} md={6}>
                          <TextField
                            label="Routing Number"
                            fullWidth
                            value={paymentForm.routingNumber}
                            onChange={(event) =>
                              onPaymentFieldChange('routingNumber', event.target.value)
                            }
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <TextField
                            label="Account Number"
                            fullWidth
                            value={paymentForm.accountNumber}
                            onChange={(event) =>
                              onPaymentFieldChange('accountNumber', event.target.value)
                            }
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <TextField
                            label="Name On Account"
                            fullWidth
                            value={paymentForm.nameOnAccount}
                            onChange={(event) =>
                              onPaymentFieldChange('nameOnAccount', event.target.value)
                            }
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <FormControl fullWidth>
                            <InputLabel id="account-type-label">Account Type</InputLabel>
                            <Select
                              labelId="account-type-label"
                              label="Account Type"
                              value={paymentForm.accountType}
                              onChange={(event) =>
                                onPaymentFieldChange(
                                  'accountType',
                                  String(event.target.value)
                                )
                              }
                            >
                              <MenuItem value={BankAccountTypeEnum.CHECKING}>
                                Checking
                              </MenuItem>
                              <MenuItem value={BankAccountTypeEnum.SAVINGS}>
                                Savings
                              </MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>
                      </>
                    ) : (
                      <>
                        <Grid item xs={12} md={6}>
                          <TextField
                            label="Card Number"
                            fullWidth
                            value={paymentForm.cardNumber}
                            error={
                              paymentForm.cardNumber.trim() !== '' &&
                              paymentCardNumberError !== ''
                            }
                            helperText={
                              paymentForm.cardNumber.trim() !== '' &&
                              paymentCardNumberError !== ''
                                ? paymentCardNumberError
                                : ' '
                            }
                            onChange={(event) =>
                              onPaymentFieldChange('cardNumber', event.target.value)
                            }
                            InputProps={{
                              inputComponent: TextMaskCreditCard as any,
                            }}
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <TextField
                            label="Expiration Date"
                            fullWidth
                            value={paymentForm.expirationDate}
                            error={
                              paymentForm.expirationDate.trim() !== '' &&
                              paymentExpirationError !== ''
                            }
                            helperText={
                              paymentForm.expirationDate.trim() !== '' &&
                              paymentExpirationError !== ''
                                ? paymentExpirationError
                                : ' '
                            }
                            onChange={(event) =>
                              onPaymentFieldChange(
                                'expirationDate',
                                event.target.value
                              )
                            }
                            InputProps={{
                              inputComponent: TextMaskCreditCardDate as any,
                            }}
                          />
                        </Grid>
                      </>
                    )}
                  </Grid>
                  <ActionsRow>
                    <Button
                      variant="outlined"
                      onClick={handlePaymentCreate}
                      disabled={busyAction != null || !isPaymentFormValid}
                    >
                      {busyAction === 'payment-create'
                        ? 'Creating...'
                        : 'Create payment profile'}
                    </Button>
                    <Button
                      variant="contained"
                      onClick={handlePaymentUpdate}
                      disabled={
                        busyAction != null ||
                        !selectedPaymentProfileId ||
                        !isPaymentFormValid
                      }
                    >
                      {busyAction === 'payment-update'
                        ? 'Updating...'
                        : 'Update selected profile'}
                    </Button>
                  </ActionsRow>
                </SectionCard>
              ) : null}

              {tab === 2 ? (
                <SectionCard>
                  <SectionHeader>
                    <Typography variant="h6">Create Transaction</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Run a one-time charge against any saved payment profile for this customer.
                    </Typography>
                  </SectionHeader>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth>
                        <InputLabel id="transaction-payment-profile-label">
                          Payment profile
                        </InputLabel>
                        <Select
                          labelId="transaction-payment-profile-label"
                          label="Payment profile"
                          value={transactionForm.customerPaymentProfileId}
                          onChange={(event) =>
                            onTransactionFieldChange(
                              'customerPaymentProfileId',
                              String(event.target.value)
                            )
                          }
                        >
                          {paymentProfiles.map((paymentProfile) => (
                            <MenuItem
                              key={paymentProfile.customerPaymentProfileId}
                              value={paymentProfile.customerPaymentProfileId}
                            >
                              {`#${paymentProfile.customerPaymentProfileId} • ${
                                paymentProfile.paymentMethod === 'bankAccount'
                                  ? paymentProfile.bankAccount.accountNumber
                                  : paymentProfile.creditCard.cardNumber
                              }`}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="Amount"
                        fullWidth
                        value={transactionForm.amount}
                        error={
                          transactionForm.amount.trim() !== '' &&
                          (!Number.isFinite(transactionAmountValue) ||
                            transactionAmountValue <= 0)
                        }
                        onChange={(event) =>
                          onTransactionFieldChange('amount', event.target.value)
                        }
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="Item Name"
                        fullWidth
                        value={transactionForm.itemName}
                        error={
                          transactionForm.itemName.trim() === '' &&
                          transactionValidationMessage === 'Item name is required.'
                        }
                        onChange={(event) =>
                          onTransactionFieldChange('itemName', event.target.value)
                        }
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="Quantity"
                        fullWidth
                        value={transactionForm.quantity}
                        error={
                          transactionForm.quantity.trim() !== '' &&
                          (!Number.isInteger(transactionQuantityValue) ||
                            transactionQuantityValue <= 0)
                        }
                        onChange={(event) =>
                          onTransactionFieldChange('quantity', event.target.value)
                        }
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        label="Description"
                        fullWidth
                        value={transactionForm.description}
                        error={
                          transactionForm.description.trim() === '' &&
                          transactionValidationMessage === 'Description is required.'
                        }
                        onChange={(event) =>
                          onTransactionFieldChange('description', event.target.value)
                        }
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        label="Notes"
                        fullWidth
                        multiline
                        minRows={2}
                        value={transactionForm.itemDescription}
                        onChange={(event) =>
                          onTransactionFieldChange(
                            'itemDescription',
                            event.target.value
                          )
                        }
                      />
                    </Grid>
                  </Grid>
                  <ActionsRow>
                    <Typography variant="caption" color="text.secondary">
                      Existing transactions on file: {transactions.length}
                    </Typography>
                    <Button
                      variant="contained"
                      onClick={handleTransactionCreate}
                      disabled={busyAction != null || !isTransactionFormValid}
                    >
                      {busyAction === 'transaction-create'
                        ? 'Creating...'
                        : 'Create transaction'}
                    </Button>
                  </ActionsRow>
                </SectionCard>
              ) : null}

              {tab === 3 ? (
                <SectionCard>
                  <SectionHeader>
                    <Typography variant="h6">Subscriptions</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Create a new recurring charge, update a selected subscription, or cancel it directly in Authorize.Net.
                    </Typography>
                  </SectionHeader>
                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 2 }}>
                    <FormControl fullWidth>
                      <InputLabel id="subscription-select-label">Existing subscription</InputLabel>
                      <Select
                        labelId="subscription-select-label"
                        label="Existing subscription"
                        value={selectedSubscriptionId}
                        onChange={(event) =>
                          setSelectedSubscriptionId(String(event.target.value))
                        }
                      >
                        <MenuItem value="">Create new subscription</MenuItem>
                        {subscriptions.map((subscription) => (
                          <MenuItem
                            key={subscription.subscriptionId}
                            value={subscription.subscriptionId}
                          >
                            {`#${subscription.subscriptionId} • ${subscription.name} • ${subscription.status}`}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <FormControl fullWidth>
                      <InputLabel id="subscription-payment-profile-label">
                        Payment profile
                      </InputLabel>
                      <Select
                        labelId="subscription-payment-profile-label"
                        label="Payment profile"
                        value={subscriptionForm.customerPaymentProfileId}
                        onChange={(event) =>
                          onSubscriptionFieldChange(
                            'customerPaymentProfileId',
                            String(event.target.value)
                          )
                        }
                      >
                        {paymentProfiles.map((paymentProfile) => (
                          <MenuItem
                            key={paymentProfile.customerPaymentProfileId}
                            value={paymentProfile.customerPaymentProfileId}
                          >
                            {`#${paymentProfile.customerPaymentProfileId}`}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <FormControl fullWidth>
                      <InputLabel id="subscription-address-label">Shipping profile</InputLabel>
                      <Select
                        labelId="subscription-address-label"
                        label="Shipping profile"
                        value={subscriptionForm.customerAddressId}
                        onChange={(event) =>
                          onSubscriptionFieldChange(
                            'customerAddressId',
                            String(event.target.value)
                          )
                        }
                      >
                        {shippingAddresses.map((address) => (
                          <MenuItem
                            key={address.customerAddressId}
                            value={address.customerAddressId}
                          >
                            {`#${address.customerAddressId} • ${address.address}`}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Stack>
                  <Grid container spacing={2} sx={{ mb: 0.5 }}>
                    <Grid item xs={12}>
                      <FormControl
                        fullWidth
                        error={!isSubscriptionFormValid && (!subscriptionForm.unitId || selectedSubscriptionUnit == null)}
                      >
                        <InputLabel id="subscription-unit-label">Unit</InputLabel>
                        <Select
                          labelId="subscription-unit-label"
                          label="Unit"
                          value={subscriptionForm.unitId}
                          onChange={(event) =>
                            onSubscriptionFieldChange('unitId', String(event.target.value))
                          }
                        >
                          <MenuItem value="">Select unit</MenuItem>
                          {subscriptionUnitOptions.map((unit) => (
                            <MenuItem
                              key={unit.id}
                              value={unit.id}
                              disabled={
                                unit.status === 'assignedToCustomer' &&
                                unit.id !== subscriptionForm.unitId
                              }
                            >
                              {`${unit.formattedValue} • $${unit.price.toFixed(2)}`}
                            </MenuItem>
                          ))}
                        </Select>
                        <FormHelperText>
                          Pick the unit first. The subscription name, description, and monthly price are locked to the selected unit.
                        </FormHelperText>
                      </FormControl>
                    </Grid>
                  </Grid>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                      <TextField
                        label="Start Date"
                        type="date"
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                        value={subscriptionForm.startDate}
                        error={!isSubscriptionFormValid && !hasText(subscriptionForm.startDate)}
                        helperText={
                          !hasText(subscriptionForm.startDate)
                            ? 'Required'
                            : ' '
                        }
                        onChange={(event) =>
                          onSubscriptionFieldChange('startDate', event.target.value)
                        }
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        label="Interval Length"
                        fullWidth
                        value={subscriptionForm.intervalLength}
                        error={
                          !Number.isInteger(intervalLengthValue) ||
                          intervalLengthValue <= 0
                        }
                        helperText="Whole number greater than zero."
                        onChange={(event) =>
                          onSubscriptionFieldChange(
                            'intervalLength',
                            event.target.value
                          )
                        }
                      />
                    </Grid>
                  </Grid>
                  {subscriptionValidationMessage !== '' ? (
                    <Alert severity="warning" sx={{ mt: 2 }}>
                      {subscriptionValidationMessage}
                    </Alert>
                  ) : null}
                  <ActionsRow>
                    <Stack direction="row" spacing={1}>
                      <Button
                        variant="outlined"
                        onClick={handleSubscriptionCreate}
                        disabled={busyAction != null || !isSubscriptionFormValid}
                      >
                        {busyAction === 'subscription-create'
                          ? 'Creating...'
                          : 'Create subscription'}
                      </Button>
                      <Button
                        variant="contained"
                        onClick={handleSubscriptionUpdate}
                        disabled={
                          busyAction != null ||
                          !subscriptionForm.subscriptionId ||
                          !isSubscriptionFormValid
                        }
                      >
                        {busyAction === 'subscription-update'
                          ? 'Updating...'
                          : 'Update selected'}
                      </Button>
                    </Stack>
                    <Button
                      variant="outlined"
                      color="error"
                      onClick={handleSubscriptionCancel}
                      disabled={busyAction != null || !subscriptionForm.subscriptionId}
                    >
                      {busyAction === 'subscription-cancel'
                        ? 'Cancelling...'
                        : 'Cancel selected'}
                    </Button>
                  </ActionsRow>
                  <Divider sx={{ my: 2.5 }} />
                  <SectionHeader sx={{ mb: 1.5 }}>
                    <Typography variant="h6">Rental Details</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Keep admin-only notes on the rental. Covered parking rentals can also store vehicle registration numbers and photo attachments.
                    </Typography>
                  </SectionHeader>
                  {!subscriptionForm.subscriptionId || !selectedSubscription ? (
                    <Alert severity="info">
                      Save or select an existing subscription before managing admin-only rental details.
                    </Alert>
                  ) : (
                    <ContentStack>
                      <Box>
                        <SectionHeader sx={{ mb: 1 }}>
                          <Typography variant="subtitle1" fontWeight={700}>
                            Notes
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Notes are only visible inside the admin workbench.
                          </Typography>
                        </SectionHeader>
                        <TextField
                          label="Rental note"
                          fullWidth
                          multiline
                          minRows={3}
                          value={rentalNoteForm.text}
                          onChange={(event) =>
                            onRentalNoteFieldChange('text', event.target.value)
                          }
                        />
                        <Stack
                          direction="row"
                          spacing={1}
                          justifyContent="flex-end"
                          sx={{ mt: 1.5 }}
                        >
                          {rentalNoteForm.noteId ? (
                            <Button
                              variant="text"
                              onClick={() => setRentalNoteForm(EMPTY_RENTAL_NOTE_FORM)}
                              disabled={busyAction != null}
                            >
                              Cancel edit
                            </Button>
                          ) : null}
                          <Button
                            variant="contained"
                            onClick={handleRentalNoteSave}
                            disabled={busyAction != null || !hasText(rentalNoteForm.text)}
                          >
                            {busyAction === 'rental-note-save'
                              ? 'Saving...'
                              : rentalNoteForm.noteId
                                ? 'Update note'
                                : 'Add note'}
                          </Button>
                        </Stack>
                        <DetailList>
                          {(selectedRentalDetail?.notes ?? []).map((note) => (
                            <Box
                              key={note.id}
                              sx={{
                                border: (theme) => `1px solid ${theme.palette.divider}`,
                                borderRadius: 1.25,
                                p: 1.5,
                              }}
                            >
                              <Typography
                                variant="body2"
                                sx={{ whiteSpace: 'pre-wrap' }}
                              >
                                {note.text}
                              </Typography>
                              <Stack
                                direction={{ xs: 'column', sm: 'row' }}
                                spacing={1}
                                justifyContent="space-between"
                                alignItems={{ xs: 'flex-start', sm: 'center' }}
                                sx={{ mt: 1 }}
                              >
                                <Typography variant="caption" color="text.secondary">
                                  {`Updated ${formatDateTime(note.updatedAt)}`}
                                </Typography>
                                <Stack direction="row" spacing={1}>
                                  <Button
                                    size="small"
                                    startIcon={<EditOutlinedIcon />}
                                    onClick={() =>
                                      setRentalNoteForm({
                                        noteId: note.id,
                                        text: note.text,
                                      })
                                    }
                                    disabled={busyAction != null}
                                  >
                                    Edit
                                  </Button>
                                  <Button
                                    size="small"
                                    color="error"
                                    startIcon={<DeleteOutlineIcon />}
                                    onClick={() => handleRentalNoteDelete(note.id)}
                                    disabled={busyAction != null}
                                  >
                                    Delete
                                  </Button>
                                </Stack>
                              </Stack>
                            </Box>
                          ))}
                          {(selectedRentalDetail?.notes ?? []).length === 0 ? (
                            <Typography variant="body2" color="text.secondary">
                              No notes have been added for this rental yet.
                            </Typography>
                          ) : null}
                        </DetailList>
                      </Box>
                      <Divider />
                      <Box>
                        <SectionHeader sx={{ mb: 1 }}>
                          <Typography variant="subtitle1" fontWeight={700}>
                            Vehicle Registrations
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {isCoveredParkingRental
                              ? 'Covered parking rentals can store multiple registration numbers with optional photo attachments.'
                              : 'This rental is not marked as covered parking, so vehicle registration storage is disabled.'}
                          </Typography>
                        </SectionHeader>
                        {isCoveredParkingRental ? (
                          <>
                            <TextField
                              label="Registration number"
                              fullWidth
                              value={rentalVehicleForm.registrationNumber}
                              onChange={(event) =>
                                onRentalVehicleFieldChange(
                                  'registrationNumber',
                                  event.target.value
                                )
                              }
                            />
                            <Stack
                              direction="row"
                              spacing={1}
                              justifyContent="flex-end"
                              sx={{ mt: 1.5 }}
                            >
                              {rentalVehicleForm.vehicleId ? (
                                <Button
                                  variant="text"
                                  onClick={() =>
                                    setRentalVehicleForm(EMPTY_RENTAL_VEHICLE_FORM)
                                  }
                                  disabled={busyAction != null}
                                >
                                  Cancel edit
                                </Button>
                              ) : null}
                              <Button
                                variant="contained"
                                onClick={handleRentalVehicleSave}
                                disabled={
                                  busyAction != null ||
                                  !hasText(rentalVehicleForm.registrationNumber)
                                }
                              >
                                {busyAction === 'rental-vehicle-save'
                                  ? 'Saving...'
                                  : rentalVehicleForm.vehicleId
                                    ? 'Update vehicle'
                                    : 'Add vehicle'}
                              </Button>
                            </Stack>
                          </>
                        ) : (
                          <Alert severity="info">
                            Select a covered parking unit if you need to track vehicles for this rental.
                          </Alert>
                        )}
                        <DetailList>
                          {(selectedRentalDetail?.vehicles ?? []).map((vehicle) => (
                            <Box
                              key={vehicle.id}
                              sx={{
                                border: (theme) => `1px solid ${theme.palette.divider}`,
                                borderRadius: 1.25,
                                p: 1.5,
                              }}
                            >
                              <Stack
                                direction={{ xs: 'column', sm: 'row' }}
                                spacing={1}
                                justifyContent="space-between"
                                alignItems={{ xs: 'flex-start', sm: 'center' }}
                              >
                                <Box>
                                  <Typography variant="body1" fontWeight={700}>
                                    {vehicle.registrationNumber}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {`Updated ${formatDateTime(vehicle.updatedAt)}`}
                                  </Typography>
                                </Box>
                                <Stack direction="row" spacing={1}>
                                  <Button
                                    size="small"
                                    startIcon={<EditOutlinedIcon />}
                                    onClick={() =>
                                      setRentalVehicleForm({
                                        vehicleId: vehicle.id,
                                        registrationNumber: vehicle.registrationNumber,
                                      })
                                    }
                                    disabled={busyAction != null}
                                  >
                                    Edit
                                  </Button>
                                  <Button
                                    size="small"
                                    color="error"
                                    startIcon={<DeleteOutlineIcon />}
                                    onClick={() => handleRentalVehicleDelete(vehicle.id)}
                                    disabled={busyAction != null}
                                  >
                                    Delete
                                  </Button>
                                </Stack>
                              </Stack>
                              <Stack
                                direction={{ xs: 'column', md: 'row' }}
                                spacing={1}
                                alignItems={{ xs: 'stretch', md: 'center' }}
                                sx={{ mt: 1.5 }}
                              >
                                <Button
                                  variant="outlined"
                                  component="label"
                                  startIcon={<FileUploadOutlinedIcon />}
                                  disabled={busyAction != null}
                                >
                                  Upload photo
                                  <input
                                    hidden
                                    type="file"
                                    accept="image/*"
                                    onChange={(event) => {
                                      const file = event.target.files?.[0]
                                      if (file) {
                                        void handleVehicleAttachmentUpload(vehicle, file)
                                      }
                                      event.currentTarget.value = ''
                                    }}
                                  />
                                </Button>
                                <Typography variant="caption" color="text.secondary">
                                  JPG, PNG, WEBP, HEIC. Max 5MB.
                                </Typography>
                              </Stack>
                              <Stack
                                direction="row"
                                spacing={1}
                                flexWrap="wrap"
                                sx={{ mt: 1.5 }}
                              >
                                {vehicle.attachments.map((attachment) => (
                                  <Box
                                    key={attachment.id}
                                    sx={{
                                      border: (theme) =>
                                        `1px solid ${theme.palette.divider}`,
                                      borderRadius: 1,
                                      p: 1,
                                      width: { xs: '100%', sm: 180 },
                                    }}
                                  >
                                    <Box
                                      component="img"
                                      alt={attachment.fileName}
                                      src={getAttachmentDownloadUrl(
                                        vehicle.id,
                                        attachment.id
                                      )}
                                      sx={{
                                        width: '100%',
                                        height: 112,
                                        objectFit: 'cover',
                                        borderRadius: 0.75,
                                        backgroundColor: 'rgba(255,255,255,0.04)',
                                      }}
                                    />
                                    <Stack spacing={1} sx={{ mt: 1 }}>
                                      <Chip
                                        size="small"
                                        label={attachment.fileName}
                                        sx={{ maxWidth: '100%' }}
                                      />
                                      <Stack direction="row" spacing={1}>
                                        <Button
                                          size="small"
                                          component="a"
                                          href={getAttachmentDownloadUrl(
                                            vehicle.id,
                                            attachment.id
                                          )}
                                          target="_blank"
                                          rel="noreferrer"
                                        >
                                          Open
                                        </Button>
                                        <Button
                                          size="small"
                                          color="error"
                                          onClick={() =>
                                            handleVehicleAttachmentDelete(
                                              vehicle.id,
                                              attachment.id
                                            )
                                          }
                                          disabled={busyAction != null}
                                        >
                                          Delete
                                        </Button>
                                      </Stack>
                                    </Stack>
                                  </Box>
                                ))}
                                {vehicle.attachments.length === 0 ? (
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                  >
                                    No photos uploaded for this vehicle yet.
                                  </Typography>
                                ) : null}
                              </Stack>
                            </Box>
                          ))}
                          {(selectedRentalDetail?.vehicles ?? []).length === 0 ? (
                            <Typography variant="body2" color="text.secondary">
                              No vehicle registrations have been added for this rental yet.
                            </Typography>
                          ) : null}
                        </DetailList>
                      </Box>
                    </ContentStack>
                  )}
                </SectionCard>
              ) : null}
            </ContentStack>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  )
}
